import { EyeOpenIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  ITriggeredWorkflow,
  IWorkflowRun,
} from "../../../../@types/admin/AdminWorkflow";
import { TableSorted } from "../../../../components/TableSorted";
import {
  usePatientWorkflowHistory,
  useWorkflowRunSteps,
} from "../../../../hooks/queries/admin/workflows";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { AdminBreadcrumb, IAdminBreadcrumbItem } from "../AdminBreadcrumb";
import {
  ErrorMessage,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  RowActionGroup,
  StatusBadge,
} from "../AdminPage.styles";
import { RowAction } from "../RowAction";
import { TableContainer } from "../TableContainer";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { humanizeEnumName } from "./workflowStep";
import { WorkflowStepTimeline } from "./WorkflowStepTimeline";

interface IWorkflowDrillDownModalProps {
  workflow: ITriggeredWorkflow;
}

type ILevel = "runs" | "steps";

/** "InProgress" is in flight; "Done" finished; "Restarted" was superseded. */
function runTone(status: string): "active" | "inactive" | "warning" {
  if (status === "Done") return "active";
  if (status === "InProgress") return "warning";
  return "inactive";
}

/**
 * One patient's workflow runs, and the steps of the run you pick (EN-5167).
 *
 * Retool used four modal frames for this. The portal has a single global
 * modal slot (EN-5149), so the drill-down walks its own levels behind a
 * breadcrumb.
 */
export function WorkflowDrillDownModal({
  workflow,
}: IWorkflowDrillDownModalProps) {
  const [level, setLevel] = useState<ILevel>("runs");
  const [run, setRun] = useState<IWorkflowRun>();

  const { runs, isLoading, isError } = usePatientWorkflowHistory(
    workflow.patientId
  );
  const {
    steps,
    isLoading: stepsLoading,
    isError: stepsError,
  } = useWorkflowRunSteps(
    level === "steps" ? run?.workflowPatientStateId : undefined
  );

  const goToRuns = () => {
    setLevel("runs");
    setRun(undefined);
  };

  const columns: ColumnDef<IWorkflowRun, string>[] = useMemo(
    () => [
      { accessorKey: "workflowName", header: "Workflow", enableSorting: true },
      {
        id: "status",
        header: "Status",
        accessorFn: (item) => item.status,
        cell: ({ row }) => (
          <StatusBadge status={runTone(row.original.status)}>
            {humanizeEnumName(row.original.status)}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "published",
        header: "Definition",
        // TableSorted renders "-" and skips the cell renderer for a falsy
        // value, so this goes through an accessorFn returning a string.
        accessorFn: (item) => (item.published ? "Published" : "Unpublished"),
        enableSorting: true,
      },
      {
        accessorKey: "triggeredDate",
        header: "Triggered",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.triggeredDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastUpdatedDate",
        header: "Last updated",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUpdatedDate, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="View steps"
              icon={<EyeOpenIcon />}
              onClick={() => {
                setRun(row.original);
                setLevel("steps");
              }}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  const breadcrumb: IAdminBreadcrumbItem[] = [
    {
      label: workflow.patientName ?? `Patient ${workflow.patientId}`,
      onClick: level === "steps" ? goToRuns : undefined,
    },
  ];
  if (level === "steps" && run) {
    breadcrumb.push({ label: run.workflowName ?? "Run" });
  }

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Workflow runs</ModalTitle>
        <AdminBreadcrumb items={breadcrumb} ariaLabel="Workflow drill-down" />
      </ModalHeader>

      {level === "runs" && (
        <>
          <ModalSubtitle>
            Every workflow triggered for this patient, newest first.
          </ModalSubtitle>

          {isError && (
            <ErrorMessage>
              Failed to load this patient&apos;s workflow runs. Close the dialog
              and try again.
            </ErrorMessage>
          )}

          <TableContainer>
            <TableWrapper
              refreshKeys={[
                ["admin-workflow-history"],
                ["admin-workflow-run-steps"],
              ]}
            >
              <TableSorted
                columns={columns}
                data={runs}
                emptyStateText={
                  isLoading ? <TableLoadingState /> : "No workflow runs found"
                }
              />
            </TableWrapper>
          </TableContainer>
        </>
      )}

      {level === "steps" && run && (
        <>
          <ModalSubtitle>
            {run.workflowDescription ??
              "The steps that ran, in the order they ran."}
          </ModalSubtitle>
          <WorkflowStepTimeline
            steps={steps}
            isLoading={stepsLoading}
            isError={stepsError}
          />
        </>
      )}
    </ModalContainer>
  );
}
